/**
 * UUID Version 1 Decoder
 *
 * UUID v1 is a time-based UUID format defined in RFC 9562 (previously RFC 4122).
 * Format: xxxxxxxx-xxxx-1xxx-yxxx-xxxxxxxxxxxx
 *
 * Structure:
 * - 32 bits: time_low (low field of timestamp)
 * - 16 bits: time_mid (middle field of timestamp)
 * - 4 bits: Version (0001 = 1)
 * - 12 bits: time_hi (high field of timestamp)
 * - 2 bits: Variant (10)
 * - 14 bits: clock_seq (clock sequence)
 * - 48 bits: node (typically MAC address)
 *
 * The timestamp is a 60-bit value representing 100-nanosecond intervals
 * since October 15, 1582 00:00:00 UTC (the UUID epoch).
 *
 * @see https://www.rfc-editor.org/rfc/rfc9562.html#name-uuid-version-1
 */

export interface UUIDv1DecodeResult {
  isValid: boolean;
  uuid?: string;
  version?: number;
  variant?: string;
  timestamp?: number;
  timestampHex?: string;
  date?: string;
  clockSequence?: number;
  clockSequenceHex?: string;
  node?: string;
  nodeFormatted?: string;
  error?: string;
}

// UUID v1 epoch: October 15, 1582 00:00:00 UTC
// Offset from Unix epoch in 100-nanosecond intervals
const UUID_V1_EPOCH_OFFSET = 122192928000000000n;

/**
 * Validates if the input string is a valid UUID v1
 */
export function isValidUUIDv1(input: string): boolean {
  if (!input) return false;

  // Remove any whitespace
  const trimmed = input.trim();

  // UUID format: 8-4-4-4-12 (36 characters total with hyphens)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  if (!uuidRegex.test(trimmed)) {
    return false;
  }

  // Check version (13th character should be '1')
  const versionChar = trimmed.charAt(14); // Position 14 (0-indexed) is after "xxxxxxxx-xxxx-"
  if (versionChar !== '1') {
    return false;
  }

  // Check variant (17th hex digit should be 8, 9, a, or b)
  const variantChar = trimmed.charAt(19).toLowerCase(); // Position 19 is after "xxxxxxxx-xxxx-1xxx-"
  if (!['8', '9', 'a', 'b'].includes(variantChar)) {
    return false;
  }

  // Validate timestamp is reasonable
  try {
    const hex = trimmed.replace(/-/g, '');

    // Extract timestamp components
    const timeLow = hex.substring(0, 8);
    const timeMid = hex.substring(8, 12);
    const timeHiAndVersion = hex.substring(12, 16);

    // Extract time_hi (12 bits, ignoring the 4-bit version)
    const timeHi = timeHiAndVersion.substring(1, 4);

    // Reconstruct 60-bit timestamp: time_hi (12 bits) + time_mid (16 bits) + time_low (32 bits)
    const timestampHex = timeHi + timeMid + timeLow;
    const timestampBigInt = BigInt('0x' + timestampHex);

    // Convert to Unix timestamp (milliseconds)
    const unixTimestamp = Number((timestampBigInt - UUID_V1_EPOCH_OFFSET) / 10000n);

    // Validate timestamp is within reasonable range
    // UUID v1 was defined in RFC 4122 (2005), but allow for earlier implementations (1990s)
    const minTimestamp = new Date('1990-01-01T00:00:00Z').getTime();
    const now = Date.now();
    const maxTimestamp = now + (365 * 24 * 60 * 60 * 1000 * 10); // 10 years in future

    if (unixTimestamp < minTimestamp || unixTimestamp > maxTimestamp) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Decodes a UUID v1 and extracts its components
 */
export function decodeUUIDv1(input: string): UUIDv1DecodeResult {
  if (!input) {
    return {
      isValid: false,
      error: 'Input is empty'
    };
  }

  const trimmed = input.trim();

  if (!isValidUUIDv1(trimmed)) {
    return {
      isValid: false,
      error: 'Invalid UUID v1 format'
    };
  }

  try {
    // Normalize to lowercase
    const uuid = trimmed.toLowerCase();

    // Extract components (remove hyphens for easier parsing)
    const hex = uuid.replace(/-/g, '');

    // Extract timestamp components
    const timeLow = hex.substring(0, 8);
    const timeMid = hex.substring(8, 12);
    const timeHiAndVersion = hex.substring(12, 16);

    // Version: first nibble of time_hi_and_version
    const versionHex = timeHiAndVersion.substring(0, 1);
    const version = parseInt(versionHex, 16);

    // Time high: last 3 nibbles of time_hi_and_version
    const timeHi = timeHiAndVersion.substring(1, 4);

    // Reconstruct 60-bit timestamp
    const timestampHex = timeHi + timeMid + timeLow;
    const timestampBigInt = BigInt('0x' + timestampHex);

    // Convert to Unix timestamp (milliseconds)
    const unixTimestamp = Number((timestampBigInt - UUID_V1_EPOCH_OFFSET) / 10000n);
    const date = new Date(unixTimestamp).toISOString();

    // Extract clock sequence (14 bits)
    const clockSeqAndVariant = hex.substring(16, 20);
    const clockSeqHigh = parseInt(clockSeqAndVariant.substring(0, 2), 16);
    const clockSeqLow = parseInt(clockSeqAndVariant.substring(2, 4), 16);

    // Variant: top 2 bits of clock_seq_hi_and_reserved
    const variantValue = clockSeqHigh;
    let variant = 'Unknown';
    if ((variantValue & 0b11000000) === 0b10000000) {
      variant = 'RFC 9562 (10xx)';
    }

    // Clock sequence: remove variant bits (top 2 bits)
    const clockSeqHighBits = clockSeqHigh & 0b00111111;
    const clockSequence = (clockSeqHighBits << 8) | clockSeqLow;
    const clockSequenceHex = clockSequence.toString(16).padStart(4, '0');

    // Node: 48 bits (typically MAC address)
    const node = hex.substring(20, 32);
    const nodeFormatted = node.match(/.{2}/g)?.join(':') || node;

    return {
      isValid: true,
      uuid,
      version,
      variant,
      timestamp: unixTimestamp,
      timestampHex,
      date,
      clockSequence,
      clockSequenceHex,
      node,
      nodeFormatted
    };
  } catch (error) {
    return {
      isValid: false,
      error: `Decoding error: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Formats the decoded UUID v1 result for display
 */
export function formatUUIDv1Result(result: UUIDv1DecodeResult): string {
  if (!result.isValid) {
    return `❌ ${result.error || 'Invalid UUID v1'}`;
  }

  const lines = [
    '✅ Valid UUID Version 1',
    '',
    `UUID: ${result.uuid}`,
    `Version: ${result.version}`,
    `Variant: ${result.variant}`,
    '',
    '📅 Timestamp Information:',
    `  Timestamp (ms): ${result.timestamp}`,
    `  Timestamp (hex): 0x${result.timestampHex}`,
    `  Date (ISO): ${result.date}`,
    '',
    '🔢 Additional Components:',
    `  Clock Sequence: ${result.clockSequence} (0x${result.clockSequenceHex})`,
    `  Node (MAC): ${result.nodeFormatted}`,
    `  Node (hex): ${result.node}`,
    '',
    '📖 Learn more: https://www.rfc-editor.org/rfc/rfc9562.html#name-uuid-version-1'
  ];

  return lines.join('\n');
}
